import { useInfiniteQuery } from '@tanstack/react-query';
import { TPO_GEN_DIM_OPTIONS_QUERY_KEY } from '../../../utils/tpoGenQueryKeys';
import { tpoGenUrls } from 'config/urls';
import axios from 'axios';
import debounce from 'lodash/debounce';
import { useEffect, useMemo, useState } from 'react';
import { usePlanDetailsContext } from '../../context/PlanDetailsContext';
import { useTpoGenConfig } from '../../../context/CustomerConfigContext';

const SEARCH_DEBOUNCE_MS = 400;
const PAGE_LIMIT = 50;

interface SelectOption {
    label: string;
    value: number;
}

interface DimAttrsEntry {
    attrs: string[];
    attributeType: string;
}

const buildDimInclusion = (attrs: string[], dimension: string, attributeType: string) => ({
    inclusion: attrs.map((val) => ({
        attributeType,
        attributeDesc: val,
        attributeExId: val,
        dimension,
    })),
    exclusion: [],
});

interface SelectedDimensionType {
    value: string | string[];
    dimension: string;
    attributeType: string;
}

const fetchEventFilters = async (
    dimension: string,
    attributeType: string,
    filters?: Record<string, unknown>,
    selectedDimension?: SelectedDimensionType,
    signal?: AbortSignal,
    offset?: number,
    searchString?: string,
    planYear?: number,
    lowestAttribute?: boolean,
    dimAttrsMap: Record<string, DimAttrsEntry> = {},
) => {
    const token = localStorage.getItem('accessToken');

    const dimValue = selectedDimension?.value;
    const selectedValues = Array.isArray(dimValue) ? dimValue : dimValue ? [dimValue] : [];

    const selectedDim = (selectedDimension?.dimension as string) || '';
    const selectedInclusion = selectedValues.map((val: string) => ({
        attributeType: selectedDimension?.attributeType,
        attributeDesc: val,
        attributeExId: val,
        dimension: selectedDimension?.dimension,
    }));

    const filtersObj = Object.entries(dimAttrsMap).reduce<Record<string, unknown>>(
        (acc, [dim, { attrs, attributeType: attrType }]) => {
            const base = buildDimInclusion(attrs, dim, attrType);
            acc[dim] =
                selectedDim === dim && selectedValues.length > 0
                    ? { inclusion: [...base.inclusion, ...selectedInclusion], exclusion: [] }
                    : base;
            return acc;
        },
        {},
    );

    const response = await axios(tpoGenUrls.getEventFilters, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${token}`,
        },
        data: {
            attribute_type: attributeType,
            lowest_attribute: lowestAttribute,
            limit: PAGE_LIMIT,
            offset: offset || 0,
            search_string: searchString ?? '',
            dimension,
            filters: filtersObj,
            plan_year: planYear,
        },
        signal: signal,
    });
    return response?.data;
};

const useEventAttributeFilters = ({
    dimension,
    attributeType,
    filters,
    lowestAttribute,
    selectedDimension,
    searchString = '',
    enabled = true,
}: {
    dimension: string;
    attributeType: string;
    filters: Record<string, unknown>;
    lowestAttribute: boolean;
    selectedDimension: SelectedDimensionType;
    searchString: string;
    enabled?: boolean;
}) => {
    const [debouncedSearchString, setDebouncedSearchString] = useState(searchString);
    const { currentPlanDetails: planDetails } = usePlanDetailsContext();
    const { planFields } = useTpoGenConfig();

    const debouncedSetSearchString = useMemo(
        () =>
            debounce((value: string) => {
                setDebouncedSearchString(value);
            }, SEARCH_DEBOUNCE_MS),
        [],
    );

    useEffect(() => {
        debouncedSetSearchString(searchString);
        return () => {
            debouncedSetSearchString.cancel();
        };
    }, [searchString, debouncedSetSearchString]);

    const dimAttrsMap = useMemo(() => {
        const map: Record<string, DimAttrsEntry> = {};
        for (const field of planFields) {
            if (field.type !== 'dimSelect' || !('apiKey' in field) || !field.apiKey) continue;
            const attrs = (planDetails?.[field.apiKey as keyof typeof planDetails] as string[]) ?? [];
            map[field.dimension] = { attrs, attributeType: field.attributeType };
        }
        return map;
    }, [planFields, planDetails]);

    const { data, isPending, isFetching, isFetchingNextPage, isError, hasNextPage, fetchNextPage } = useInfiniteQuery({
        queryKey: [
            TPO_GEN_DIM_OPTIONS_QUERY_KEY,
            dimension,
            attributeType,
            filters,
            selectedDimension,
            debouncedSearchString,
            planDetails?.plan_year,
            lowestAttribute,
            searchString,
            dimAttrsMap,
        ],
        queryFn: ({ signal, pageParam }) =>
            fetchEventFilters(
                dimension,
                attributeType,
                filters,
                selectedDimension,
                signal,
                pageParam,
                debouncedSearchString,
                planDetails?.plan_year,
                lowestAttribute,
                dimAttrsMap,
            ),
        enabled,
        initialPageParam: 0,
        getNextPageParam: (lastPage, _allPages, lastPageParam) => {
            const nextOffset = lastPageParam + PAGE_LIMIT;
            return lastPage?.total_count > nextOffset ? nextOffset : undefined;
        },
    });

    const options = useMemo<SelectOption[]>(() => {
        if (!data?.pages) return [];
        return data.pages.flatMap((page) => {
            return (page.attribute_values ?? []).map((_attr: { attribute_id: string; attribute_name: string }) => ({
                label: _attr?.attribute_name,
                value: Number(_attr?.attribute_id) || _attr?.attribute_id || _attr?.attribute_name,
            }));
        });
    }, [data]);

    return {
        options,
        isPending,
        isFetching,
        isFetchingNextPage,
        isError,
        hasNextPage,
        fetchNextPage,
    };
};

export default useEventAttributeFilters;
